/**
 * Lazy-load skin concern masks from the server after detect-skin returns scores only.
 * Requests are serialized per session to avoid overlapping ONNX with detect or other masks.
 */
import { fetchJsonWithRetry } from './fetchWithRetry';
import { buildSkinAnalysisApiUrl } from './skinAnalysisApi';
import { SALON_API_PREFIX } from './apiConfig';
import { TIMEOUT_MS, RETRY } from './salonResilience';

const GENERATE_MASK_PATH = `${SALON_API_PREFIX}/generate-skin-condition-mask`;

const DISPLAY_TO_KEY = {
  Hydration: 'hydration',
  Oxygen: 'oxygen',
  Smoothness: 'smoothness',
  Dullness: 'dullness',
  'Uneven Skintone': 'uneven_skintone',
};

const inFlight = new Map();
const sessionQueues = new Map();

function conditionKeyFromDisplay(displayName) {
  if (!displayName) return null;
  if (DISPLAY_TO_KEY[displayName]) return DISPLAY_TO_KEY[displayName];
  return String(displayName).toLowerCase().replace(/\s+/g, '_');
}

function enqueueForSession(sessionId, task) {
  const key = String(sessionId || '').trim() || '__anonymous__';
  const prev = sessionQueues.get(key) || Promise.resolve();
  const next = prev.then(task, task);
  sessionQueues.set(key, next.catch(() => {}));
  return next;
}

/**
 * @param {object} params
 * @param {string} params.sessionId
 * @param {string} params.displayName
 * @param {string|null} [params.appSessionToken]
 * @returns {Promise<string|null>}
 */
export async function fetchSkinConcernMask({ sessionId, displayName, appSessionToken }) {
  if (!sessionId || !displayName) return null;
  const cacheKey = `${sessionId}:${displayName}`;
  if (inFlight.has(cacheKey)) return inFlight.get(cacheKey);

  const job = enqueueForSession(sessionId, async () => {
    try {
      const url = buildSkinAnalysisApiUrl(GENERATE_MASK_PATH);
      const data = await fetchJsonWithRetry(
        url,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: sessionId,
            condition: displayName,
            ...(appSessionToken && { app_session_token: appSessionToken }),
          }),
          timeoutMs: TIMEOUT_MS.SINGLE_DETECT,
        },
        { label: 'salon-lazy-mask', conflictMaxAttempts: RETRY.CONFLICT_MAX_ATTEMPTS }
      );
      if (data?.success && typeof data.mask === 'string' && data.mask) {
        return data.mask;
      }
      return null;
    } catch {
      return null;
    } finally {
      inFlight.delete(cacheKey);
    }
  });

  inFlight.set(cacheKey, job);
  return job;
}

export { conditionKeyFromDisplay };
